import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import { useNavigate } from "react-router-dom";
import styles from "./updates.module.css";
import Loader from "../../components/loader/loader";
import Modal from "../../components/modals/modal/Modal";
import DeleteConfirmation from "../../components/modals/deleteConfirmation/deleteConfirmation";
import GeneralSuccessPopup from "../../components/modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../../components/modals/errorPopup/errorPopup";
import UpdateFormModal from "../../components/modals/updateFormModal/updateFormModal";
import SelectContactPopup from "../../components/modals/selectContactPopup/selectContactPopup";
import BroadcastConfirmModal from "../../components/modals/broadcastConfirmModal/broadcastConfirmModal";
import { IAdminMessage } from "../../services/types/utils.types";
import { IUserReport } from "../../services/types/users.types";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  useGetAdminMessagesMutation,
  useAddOrEditAdminMessageMutation,
  useDeleteAdminMessageMutation,
  useBulkCreateAdminMessagesMutation,
} from "../../services/api/utils.api";
import { useLazyGetUsersByCompanyIdWithRecommendsQuery } from "../../services/api/user.api";
import ExportTableButtons from "../../components/exportTableButtons/ExportTableButtons";

// Role IDs for the tabs
const ROLE_IDS = {
  ADMIN: 0,
  CLIENTS: 1,
  THERAPISTS: 2,
};

type TabType = "admin" | "therapists" | "clients";
type FilterType = "all" | "broadcast" | "user-specific";

const initialMessage: Partial<IAdminMessage> = {
  Id: 0,
  Title: "",
  Desc: "",
  StatusId: 1,
  RecipientUserId: null,
};

// Helper function to parse text and convert URLs to clickable links
// Supports: [link text](url) for custom text links, and plain URLs
const renderTextWithLinks = (text: string): React.ReactNode => {
  if (!text) return null;
  
  const combinedRegex = /(\[([^\]]+)\]\((https?:\/\/[^)]+|www\.[^)]+)\))|(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;
  
  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    
    if (match[1]) {
      const linkText = match[2];
      const url = match[3];
      const href = url.startsWith('www.') ? `https://${url}` : url;
      result.push(
        <a
          key={keyIndex++}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {linkText}
        </a>
      );
    } else if (match[4]) {
      const url = match[4];
      const href = url.startsWith('www.') ? `https://${url}` : url;
      result.push(
        <a
          key={keyIndex++}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {url}
        </a>
      );
    }
    
    lastIndex = combinedRegex.lastIndex;
  }
  
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  
  return result.length > 0 ? result : text;
};

function UpdatesPage() {
  const navigate = useNavigate();
  const mobile = useMediaQuery("(max-width: 800px)");
  const admin = useAppSelector(adminSelector);
  const companyId = admin?.companyId;
  const adminRoleId = admin?.roleId;

  const [activeTab, setActiveTab] = useState<TabType>("admin");
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [messages, setMessages] = useState<IAdminMessage[]>([]);
  const [form, setForm] = useState<Partial<IAdminMessage>>(initialMessage);
  const [editId, setEditId] = useState<number | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
  const [userSelectionContext, setUserSelectionContext] = useState<{
    type: "card" | "form";
    messageId?: number;
  } | null>(null);
  const [showBroadcastConfirmModal, setShowBroadcastConfirmModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<Partial<IAdminMessage> | null>(null);
  // isSaving only turns true on the next render, so two quick clicks can both get past a
  // disabled button. A ref is set synchronously and stops the second one.
  const broadcastSavingRef = useRef(false);
  const [messageToDelete, setMessageToDelete] = useState<IAdminMessage | undefined>(undefined);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [users, setUsers] = useState<IUserReport[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{ id: number; name: string }[]>([]);
  const [sendSms, setSendSms] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);

  const [getMessages, { isLoading }] = useGetAdminMessagesMutation();
  const [addOrEditMessage, { isLoading: isSaving }] = useAddOrEditAdminMessageMutation();
  const [deleteMessage] = useDeleteAdminMessageMutation();
  const [bulkCreateMessages, { isLoading: isBulkSaving }] = useBulkCreateAdminMessagesMutation();
  const [getUsers] = useLazyGetUsersByCompanyIdWithRecommendsQuery();

  const getRoleId = useCallback((tab: TabType): number => {
    switch (tab) {
      case "admin":
        return ROLE_IDS.ADMIN;
      case "clients":
        return ROLE_IDS.CLIENTS;
      case "therapists":
        return ROLE_IDS.THERAPISTS;
      default:
        return ROLE_IDS.ADMIN;
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    if (companyId === undefined || companyId === null) return;
    
    try {
      const roleId = getRoleId(activeTab);
      // raw: this is the authoring screen, so @name must stay visible as the token the admin
      // typed rather than being resolved to whoever happens to be logged in.
      const res = await getMessages({ companyId, roleId, raw: true });
      
      if ("data" in res && res.data) {
        const activeMessages = res.data.filter((m) => m.StatusId !== 0);
        setMessages(activeMessages);
      }
    } catch (error) {
      setOpenModalError(true);
    }
  }, [companyId, activeTab, getMessages, getRoleId]);

  useEffect(() => {
    fetchMessages();
    setForm(initialMessage);
    setEditId(null);
    setShowFormModal(false);
    setFilterType("all");
  }, [fetchMessages]);

  // Notifications need at least one recipient — the endpoint used without recipients has no
  // notification fields, so a checkbox left on after the last user is removed would be
  // silently ignored while still looking active.
  useEffect(() => {
    if (selectedUsers.length === 0) {
      setSendSms(false);
      setSendEmail(false);
    }
  }, [selectedUsers.length]);

  const fetchUsersForRole = useCallback(async () => {
    if (companyId === undefined || companyId === null) return;

    try {
      // For message storage: ROLE_IDS maps tab → RoleId (0/1/2) stamped on the message.
      // For user queries: the backend uses -1 as a special signal meaning "all staff"
      // (RoleId >= 2 OR RoleId == 0). Sending [2] with isMinimal hits a dead code path and
      // returns empty. Clients (RoleId 1) are queried directly by [1].
      const queryRoleIds = activeTab === "clients" ? [1] : [-1];

      const res = await getUsers({
        companyIds: [companyId],
        roleIds: queryRoleIds,
        // -1 = no fakeCompany filter (every user in the company).
        // Super-admin, or an admin with no fakeCompanyId → -1, as in the rest of the admin app
        // ([admin.fakeCompanyId || -1]). [0] used to leave out every user assigned to a
        // fakeCompany, so they never appeared as recipients.
        // Admin with a fakeCompanyId → still scoped to that fakeCompany.
        fakeCompanyIds:
          adminRoleId !== -1 && admin?.fakeCompanyId ? [admin.fakeCompanyId] : [-1],
        isMinimal: true, // lightweight query — we only need Id/Name/Phone/Email for the picker
      });

      if ("data" in res && res.data?.Users) {
        // Updates picker should only target active users — inactive (StatusId === 0)
        // users must not receive in-app messages or SMS/email notifications.
        // Filtered here (not in the shared backend query or SelectContactPopup component)
        // so the regular users list and other popup usages still show inactive users.
        const activeUsers = res.data.Users.filter((c) => c.Users?.StatusId !== 0);

        // The backend "staff" query (roleIds [-1]) returns admins (RoleId 0) AND
        // therapists (RoleId >= 2) in one combined list, so the admin and therapists
        // tabs would otherwise overlap. Slice per tab so the picker matches the tab:
        //   therapists tab -> RoleId >= 2
        //   admin tab      -> RoleId <= 0 (company admins are RoleId 0)
        // Clients tab is already scoped server-side by roleIds [1].
        const scopedUsers =
          activeTab === "therapists"
            ? activeUsers.filter((c) => (c.Users?.RoleId ?? 0) >= 2)
            : activeTab === "admin"
            ? activeUsers.filter((c) => (c.Users?.RoleId ?? 0) <= 0)
            : activeUsers;

        setUsers(scopedUsers);
      }
    } catch (error) {
      // console.error("Failed to fetch users:", error);
    }
  }, [companyId, activeTab, admin?.fakeCompanyId, getUsers]);

  const filteredMessages = useMemo(() => {
    if (filterType === "all") return messages;
    if (filterType === "broadcast") {
      return messages.filter((m) => !m.RecipientUserId);
    }
    if (filterType === "user-specific") {
      return messages.filter((m) => m.RecipientUserId);
    }
    return messages;
  }, [messages, filterType]);

  const canEditOrDelete = useCallback(
    (message: IAdminMessage) => {
      // Global updates (no CompanyId) belong to the super admin, and the backend refuses anyone
      // else. Checking only for company admins (0) left AdminTech (-3) with live buttons that
      // ended in an error popup.
      if (adminRoleId !== -1 && (message.CompanyId === null || message.CompanyId === undefined)) {
        return false;
      }
      return true;
    },
    [adminRoleId]
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (companyId === undefined || companyId === null) {
      setOpenModalError(true);
      return;
    }

    const roleId = getRoleId(activeTab);
    const recipientUserIds = selectedUsers.map((u) => u.id);

    // Super admin creating new message - show broadcast confirmation
    if (adminRoleId === -1 && editId === null) {
      const messageToSave: Partial<IAdminMessage> = {
        Id: 0,
        Title: form.Title?.trim() ?? "",
        Desc: form.Desc ?? "",
        CompanyId: companyId,
        RoleId: roleId,
        StatusId: 1,
        RecipientUserId: recipientUserIds.length === 1 ? recipientUserIds[0] : null,
      };
      // Several recipients, or notifications requested: send through the bulk endpoint and skip
      // the broadcast modal. A message row holds a single RecipientUserId, so two or more
      // recipients sent the other way were saved as null — a message the whole role could read
      // (or every company, with "send to all"). Mirrors the condition used for regular admins.
      if (
        recipientUserIds.length > 1 ||
        (recipientUserIds.length > 0 && (sendSms || sendEmail))
      ) {
        try {
          const res = await bulkCreateMessages({
            companyId,
            roleId,
            title: form.Title?.trim() ?? "",
            desc: form.Desc ?? "",
            recipientUserIds,
            sendSms,
            sendEmail,
          });
          if ("data" in res && res.data) {
            setMessages(res.data.filter((m) => m.StatusId !== 0));
            resetForm();
            setOpenModalSuccess(true);
          } else {
            setOpenModalError(true);
          }
        } catch (error) {
          // console.error("Failed to bulk send:", error);
          setOpenModalError(true);
        }
        return;
      }
      setPendingMessage(messageToSave);
      setShowBroadcastConfirmModal(true);
      return;
    }

    try {
      // Use bulk endpoint when multiple users selected OR when SMS/email notifications are
      // requested. Never while editing: the bulk endpoint only creates — it has no update
      // path and never receives editId, so routing an edit here leaves the original row
      // untouched, adds a duplicate alongside it, and re-sends the notification.
      if (
        editId === null &&
        (recipientUserIds.length > 1 ||
          ((sendSms || sendEmail) && recipientUserIds.length > 0))
      ) {
        const res = await bulkCreateMessages({
          companyId,
          roleId,
          title: form.Title?.trim() ?? "",
          desc: form.Desc ?? "",
          recipientUserIds,
          sendSms,
          sendEmail,
        });

        if ("data" in res && res.data) {
          setMessages(res.data.filter((m) => m.StatusId !== 0));
          resetForm();
          setOpenModalSuccess(true);
        } else {
          setOpenModalError(true);
        }
      } else {
        // Single user or no user — use original endpoint
        const messageToSave: Partial<IAdminMessage> = {
          Id: editId ?? 0,
          Title: form.Title?.trim() ?? "",
          Desc: form.Desc ?? "",
          CompanyId: companyId,
          RoleId: roleId,
          StatusId: 1,
          RecipientUserId: recipientUserIds.length === 1 ? recipientUserIds[0] : null,
        };

        const res = await addOrEditMessage({
          companyId,
          roleId,
          message: messageToSave,
        });

        if ("data" in res && res.data) {
          setMessages(res.data.filter((m) => m.StatusId !== 0));
          resetForm();
          setOpenModalSuccess(true);
        } else {
          setOpenModalError(true);
        }
      }
    } catch (error) {
      // console.error("Failed to save message:", error);
      setOpenModalError(true);
    }
  };

  const handleEdit = (message: IAdminMessage) => {
    setForm({
      Title: message.Title,
      Desc: message.Desc,
      RecipientUserId: message.RecipientUserId,
    });
    // Filled from the id, not the name. A recipient with no first name has no FullName, and
    // leaving them out meant the save sent RecipientUserId = null — turning a personal update
    // into one the whole role could read, without the admin changing anything.
    setSelectedUsers(
      message.RecipientUserId
        ? [{
            id: message.RecipientUserId,
            name: message.RecipientUserName || `#${message.RecipientUserId}`,
          }]
        : []
    );
    setSendSms(false);
    setSendEmail(false);
    setEditId(message.Id);
    setShowFormModal(true);
  };

  const handleDelete = async () => {
    if (!messageToDelete || companyId === undefined || companyId === null) {
      setOpenModalError(true);
      return;
    }

    try {
      const roleId = getRoleId(activeTab);
      const res = await deleteMessage({
        companyId,
        roleId,
        message: { ...messageToDelete, StatusId: 0 },
      });

      if ("data" in res && res.data) {
        setMessages(res.data.filter((m) => m.StatusId !== 0));
        setOpenModalSuccess(true);
      } else {
        setOpenModalError(true);
      }
    } catch (error) {
      // console.error("Failed to delete message:", error);
      setOpenModalError(true);
    } finally {
      setMessageToDelete(undefined);
      setOpenDeleteModal(false);
    }
  };

  const resetForm = useCallback(() => {
    setForm(initialMessage);
    setEditId(null);
    setShowFormModal(false);
    setSelectedUsers([]);
    setSendSms(false);
    setSendEmail(false);
  }, []);

  const handleOpenAddForm = useCallback(() => {
    setForm(initialMessage);
    setEditId(null);
    setSelectedUsers([]);
    setSendSms(false);
    setSendEmail(false);
    setShowFormModal(true);
  }, []);

  const handleCloseFormModal = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleDescChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, Desc: e.target.value }));
    },
    []
  );

  const handleOpenUserSelection = useCallback(
    async (type: "card" | "form", messageId?: number) => {
      setUserSelectionContext({ type, messageId });
      await fetchUsersForRole();
      setShowUserSelectionModal(true);
    },
    [fetchUsersForRole]
  );

  const handleUsersConfirm = useCallback(
    async (contacts: IUserReport[]) => {
      if (!userSelectionContext) return;

      const usersData = contacts.map((c) => ({
        id: c.Users.Id,
        name: c.Users.FullName || `${c.Users.FirstName} ${c.Users.LastName || ""}`.trim(),
      }));

      if (userSelectionContext.type === "form") {
        // The picker lists active users only and returns only what it lists, so a recipient who
        // has since been deactivated came back unselected — and the edit saved a null
        // recipient. Anyone selected but not in the list could not have been unticked, so keep
        // them. Removing a recipient on purpose goes through the clear button.
        const hiddenSelected = selectedUsers.filter(
          (s) => !users.some((c) => c.Users.Id === s.id)
        );
        const nextSelected =
          editId !== null
            ? usersData.length > 0
              ? usersData // single-select while editing: a new pick replaces the recipient
              : hiddenSelected
            : [...usersData, ...hiddenSelected];

        setSelectedUsers(nextSelected);
        // Keep first selected user as RecipientUserId for single-user backward compat
        setForm((prev) => ({
          ...prev,
          RecipientUserId: nextSelected.length === 1 ? nextSelected[0].id : null,
        }));
        setShowUserSelectionModal(false);
      } else if (userSelectionContext.type === "card" && userSelectionContext.messageId) {
        // Card context links a single user — the picker is set to singleSelect here, so this
        // is at most one. Guarding on >= 1 rather than === 1 so a selection can never be
        // accepted and then quietly discarded.
        if (usersData.length >= 1) {
          const messageToUpdate = messages.find((m) => m.Id === userSelectionContext.messageId);
          if (messageToUpdate && companyId !== undefined && companyId !== null) {
            try {
              const roleId = getRoleId(activeTab);
              const res = await addOrEditMessage({
                companyId,
                roleId,
                message: {
                  ...messageToUpdate,
                  RecipientUserId: usersData[0].id,
                  RecipientUserName: usersData[0].name,
                },
              });

              if ("data" in res && res.data) {
                setMessages(res.data.filter((m) => m.StatusId !== 0));
                setOpenModalSuccess(true);
              }
            } catch (error) {
              // console.error("Failed to link user:", error);
              setOpenModalError(true);
            }
          }
        }
        setShowUserSelectionModal(false);
      }
    },
    [userSelectionContext, messages, companyId, activeTab, addOrEditMessage, getRoleId, selectedUsers, users, editId]
  );

  const handleRemoveUser = useCallback((userId: number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  const handleClearUsers = useCallback(() => {
    if (!userSelectionContext) return;

    if (userSelectionContext.type === "form") {
      setSelectedUsers([]);
      setForm((prev) => ({
        ...prev,
        RecipientUserId: null,
      }));
      setShowUserSelectionModal(false);
    } else if (userSelectionContext.type === "card" && userSelectionContext.messageId) {
      const messageToUpdate = messages.find((m) => m.Id === userSelectionContext.messageId);
      if (messageToUpdate && companyId !== undefined && companyId !== null) {
        (async () => {
          try {
            const roleId = getRoleId(activeTab);
            const res = await addOrEditMessage({
              companyId,
              roleId,
              message: {
                ...messageToUpdate,
                RecipientUserId: null,
                RecipientUserName: undefined,
              },
            });

            if ("data" in res && res.data) {
              setMessages(res.data.filter((m) => m.StatusId !== 0));
              setOpenModalSuccess(true);
            }
          } catch (error) {
            setOpenModalError(true);
          }
        })();
      }
      setShowUserSelectionModal(false);
    }
  }, [userSelectionContext, messages, companyId, activeTab, addOrEditMessage, getRoleId]);

  const handleBroadcastDecision = async (sendToAll: boolean) => {
    if (broadcastSavingRef.current) return;

    if (!pendingMessage || companyId === undefined || companyId === null) {
      setOpenModalError(true);
      setShowBroadcastConfirmModal(false);
      setPendingMessage(null);
      return;
    }

    broadcastSavingRef.current = true;
    try {
      const messageToSave = {
        ...pendingMessage,
        CompanyId: sendToAll ? null : companyId,
      };

      const res = await addOrEditMessage({
        companyId,
        roleId: pendingMessage.RoleId!,
        message: messageToSave,
      });

      if ("data" in res && res.data) {
        setMessages(res.data.filter((m) => m.StatusId !== 0));
        setForm(initialMessage);
        setEditId(null);
        setShowFormModal(false);
        setOpenModalSuccess(true);
      } else {
        setOpenModalError(true);
      }
    } catch (error) {
      // console.error("Failed to save message:", error);
      setOpenModalError(true);
    } finally {
      broadcastSavingRef.current = false;
      setShowBroadcastConfirmModal(false);
      setPendingMessage(null);
    }
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: "admin", label: t("ADMIN_UPDATES_TAB") },
    { key: "therapists", label: t("THERAPISTS_UPDATES_TAB") },
    { key: "clients", label: t("CLIENTS_UPDATES_TAB") },
  ];

  const filters: { key: FilterType; label: string }[] = [
    { key: "all", label: t("ALL_MESSAGES") },
    { key: "broadcast", label: t("BROADCAST_MESSAGES") },
    { key: "user-specific", label: t("USER_SPECIFIC_MESSAGES") },
  ];

  return (
    <section className={`section ${styles.section}`}>
      <BackArrowButton
        text={t("PREVIOUS_SCREEN")}
        onClick={() => navigate(-1)}
        position={"center"}
      />
      <h1 className="title title_blue">{t("ADMIN_UPDATES")}</h1>

      {/* Tabs */}
      <div className={styles.tabsContainer}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filter Buttons */}
      <div className={styles.filtersContainer}>
        {filters.map((filter) => (
          <button
            key={filter.key}
            className={`${styles.filterBtn} ${filterType === filter.key ? styles.filterActive : ""}`}
            onClick={() => setFilterType(filter.key)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* This screen gets every update untranslated, so an edit never overwrites the original
          with a translation. Say so, or an admin in another language reads it as a bug. */}
      <p className={styles.languageNote}>{t("UPDATES_SHOWN_AS_WRITTEN")}</p>

      {/* Header with Add Button */}
      <div className={styles.header}>
        <button
          type="button"
          className={styles.addBtn}
          onClick={handleOpenAddForm}
        >
          {!mobile && t("ADD_UPDATE")}
          <span className={styles.plus}>+</span>
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-start', width: '97%', marginTop: '4px', marginBottom: '10px' }}>
        <ExportTableButtons
          entity="Messages"
          filters={{ CompanyId: companyId, RoleId: getRoleId(activeTab), StatusId: 1 }}
        />
      </div>

      {/* Messages List */}
      {isLoading ? (
        <Loader />
      ) : filteredMessages.length > 0 ? (
        <div className={styles.updatesList}>
          {filteredMessages.map((message) => (
            <div key={message.Id} className={styles.updateCard}>
              <div className={styles.updateContent}>
                <div className={styles.updateHeader}>
                  {message.Title && (
                    <h3 className={styles.updateTitle}>{message.Title}</h3>
                  )}
                  {message.RecipientUserId && message.RecipientUserName && (
                    <div className={styles.userBadge}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <span>{message.RecipientUserName}</span>
                    </div>
                  )}
                </div>
                {message.Desc && (
                  <p className={styles.updateDesc}>
                    {renderTextWithLinks(message.Desc)}
                  </p>
                )}
                {message.UpdatedAt && (
                  <span className={styles.updateDate}>
                    {new Date(message.UpdatedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className={styles.updateActions}>
                <button
                  className={`${styles.actionBtn} ${styles.linkBtn}`}
                  onClick={() => handleOpenUserSelection("card", message.Id)}
                  title={t("LINK_TO_USER")}
                  disabled={!canEditOrDelete(message)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <path d="M20 8v6M23 11h-6" />
                  </svg>
                </button>
                <button
                  className={`${styles.actionBtn} ${styles.editBtn}`}
                  onClick={() => handleEdit(message)}
                  title={t("EDIT_UPDATE")}
                  disabled={!canEditOrDelete(message)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  className={`${styles.actionBtn} ${styles.deleteBtn}`}
                  onClick={() => {
                    setMessageToDelete(message);
                    setOpenDeleteModal(true);
                  }}
                  title={t("DELETE_UPDATE")}
                  disabled={!canEditOrDelete(message)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3,6 5,6 21,6" />
                    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.emptyState}>{t("NO_UPDATES")}</p>
      )}

      {/* Add/Edit Form Modal */}
      {showFormModal && (
        <Modal onClose={handleCloseFormModal}>
          <UpdateFormModal
            form={form}
            editId={editId}
            isSaving={isSaving || isBulkSaving}
            onSubmit={handleSubmit}
            onChange={handleChange}
            onDescChange={handleDescChange}
            onClose={handleCloseFormModal}
            onOpenUserSelection={() => handleOpenUserSelection("form")}
            selectedUsers={selectedUsers}
            onRemoveUser={handleRemoveUser}
            sendSms={sendSms}
            sendEmail={sendEmail}
            onToggleSms={() => setSendSms((p) => !p)}
            onToggleEmail={() => setSendEmail((p) => !p)}
          />
        </Modal>
      )}

      {/* User Selection Modal */}
      {showUserSelectionModal && (
        <Modal wide onClose={() => setShowUserSelectionModal(false)}>
          <SelectContactPopup
            contacts={users}
            // From a card, start from that update's current recipient — not the form's selection,
            // which is empty there. Without it the linked user showed unticked, and the clear
            // button (shown only when something is ticked) needed a random pick to appear.
            selectedUserIds={
              userSelectionContext?.type === "card"
                ? (() => {
                    const recipientId = messages.find(
                      (m) => m.Id === userSelectionContext.messageId
                    )?.RecipientUserId;
                    return recipientId ? [recipientId] : [];
                  })()
                : selectedUsers.map((u) => u.id)
            }
            onConfirm={handleUsersConfirm}
            onClear={handleClearUsers}
            showClearButton={true}
            // A message row holds one RecipientUserId, so both linking from a card and editing an
            // existing message can only ever store a single user. Without this, picking two while
            // editing saves a null recipient — turning a message meant for two people into one the
            // whole role can read. Creating a new message is unrestricted: it routes to the bulk
            // endpoint, which does hold many recipients.
            singleSelect={userSelectionContext?.type === "card" || editId !== null}
            onClose={() => setShowUserSelectionModal(false)}
          />
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {openDeleteModal && (
        <Modal onClose={() => setOpenDeleteModal(false)}>
          <DeleteConfirmation
            onClose={() => setOpenDeleteModal(false)}
            onDelete={handleDelete}
            text={`${t("CONFIRM_DELETE_UPDATE")}`}
          />
        </Modal>
      )}

      {/* Success Modal */}
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup onClose={() => setOpenModalSuccess(false)} />
        </Modal>
      )}

      {/* Broadcast Confirmation Modal */}
      {showBroadcastConfirmModal && (
        <Modal onClose={() => {
          setShowBroadcastConfirmModal(false);
          setPendingMessage(null);
        }}>
          <BroadcastConfirmModal
            onConfirmAll={() => handleBroadcastDecision(true)}
            onConfirmCurrent={() => handleBroadcastDecision(false)}
            isSaving={isSaving}
            onClose={() => {
              setShowBroadcastConfirmModal(false);
              setPendingMessage(null);
            }}
          />
        </Modal>
      )}

      {/* Error Modal */}
      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup onClose={() => setOpenModalError(false)} />
        </Modal>
      )}
    </section>
  );
}

export default UpdatesPage;
